import React, { useEffect, useState } from 'react';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Button, Flex } from 'antd';
import LoadingIndicator from '../LoadingIndicator';
import useQueryParams from '../../hooks/utility/useQueryParams';
import { BusinessType, PaymentMethods } from '../../services/exports/Constants';
import useApiClient from '../../hooks/api/useApiClient';
import useBusinessProfile from '../../hooks/context/useBusinessProfile';

export default function PaymentMethodForm({ disabled, returnUrl, buttonText, onSelectPaymentMethod, onSuccess }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Billing:PaymentMethodForm' });

  const { serviceProvider } = useSelector((state) => state.currentServiceProvider);

  const { business_profile, getValue } = useBusinessProfile();
  const stripe = useStripe();
  const elements = useElements();
  const { PaymentOptionsManager, StripeManager } = useApiClient();

  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const query = useQueryParams();

  const clientSecret = query.get('setup_intent_client_secret');
  const hasExtraPaymentMethods = serviceProvider.country === 'GB';

  useEffect(() => {
    if (!stripe || !clientSecret) {
      return;
    }

    stripe.retrieveSetupIntent(clientSecret).then(({ setupIntent }) => {
      switch (setupIntent.status) {
        case 'succeeded':
          return savePaymentMethod(setupIntent);

        case 'processing':
          setSuccess(true);

          return toast.success(t('toasts.processing'));

        default:
          setSuccess(false);

          return toast.error(t('toasts.failed'));
      }
    });
  }, [stripe, clientSecret]);

  const savePaymentMethod = async (setupIntent) => {
    setLoading(true);
    const { success } = await PaymentOptionsManager.create({
      payment_method_id: new URLSearchParams(window.location.search).get('payment_method_id'),
      external_payment_method_id: setupIntent.payment_method,
    });
    setLoading(false);

    if (!success) {
      setSuccess(false);

      return toast.error(t('toasts.failed_to_save_payment_method'));
    }

    onSuccess();
  };

  const createCheckoutSession = async () => {
    setRedirecting(true);
    const { success, data } = await StripeManager.createCheckoutSession({
      return_url: `${window.location.href}?checkout_session_id={CHECKOUT_SESSION_ID}`,
    });

    if (!success) {
      setRedirecting(false);

      return toast.error('toasts.failed_to_create_checkout_session');
    }

    window.location.replace(data.url);
  };

  const submit = async (e) => {
    e.preventDefault();

    setStripeLoading(true);

    // confirmSetup throws (rather than returning `error`) on integration errors,
    // e.g. when the Payment Element is not mounted.
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
      });

      if (error) {
        setSuccess(false);
        toast.error(t('toasts.failed'));
      }
    } catch {
      toast.error(t('toasts.failed'));
    } finally {
      setStripeLoading(false);
    }
  };

  const renderLoading = () => <LoadingIndicator />;

  const renderScreen = () => {
    if (loading || !stripe || !elements) {
      return renderLoading();
    }

    if (success !== null) {
      return renderResult();
    }

    return renderForm();
  };

  const renderResult = () => {
    if (success) {
      return null;
    }

    return renderForm();
  };

  const isCard = (paymentMethod) =>
    paymentMethod === PaymentMethods.CARD ||
    paymentMethod === PaymentMethods.GOOGLE_PAY ||
    paymentMethod === PaymentMethods.APPLE_PAY;

  const renderForm = () => (
    <form onSubmit={submit}>
      {stripe && elements && (
        <>
          <PaymentElement
            options={{
              wallets: {
                applePay: 'never',
                googlePay: 'never',
              },
              paymentMethodOrder: ['sepa_debit', 'card'],
              defaultValues: {
                billingDetails: {
                  name:
                    business_profile.business_type === BusinessType.Company
                      ? business_profile.legal_name
                      : business_profile.owner_full_name,
                  email: getValue('owner_email'),
                  phone: getValue('owner_phone_number'),
                  address: {
                    line1: getValue('legal_address'),
                    city: getValue('legal_city'),
                    state: getValue('legal_state'),
                    country: getValue('legal_country'),
                    postal_code: getValue('legal_zip_code'),
                  },
                },
              },
            }}
            onReady={() => setPaymentElementReady(true)}
            onLoadError={() => toast.error(t('toasts.failed_to_load'))}
            onChange={(e) =>
              onSelectPaymentMethod(isCard(e.value.type) ? PaymentMethods.CARD : PaymentMethods.SEPA_DEBIT)
            }
          />
          <Flex justify="end" className="mt-md" gap="16px">
            <Button
              htmlType="submit"
              type="primary"
              loading={stripeLoading}
              disabled={stripeLoading || !stripe || !elements || !paymentElementReady || disabled}
            >
              {buttonText}
            </Button>
            {hasExtraPaymentMethods && (
              <Button
                color="default"
                variant="solid"
                htmlType="button"
                loading={redirecting}
                disabled={stripeLoading}
                onClick={createCheckoutSession}
              >
                {t('buttons.more_payment_methods')}
              </Button>
            )}
          </Flex>
        </>
      )}
    </form>
  );

  return renderScreen();
}
